import type { ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { DEFAULT_BASIS, type Basis, type CogsMeasure, type Component, type GridResponse, type GridRow, type Market, type PriceRun, type PriceRunLine, type PriceRuns } from "./api";
import {
  ABSENT, BASIS_LABEL, cogsNullReason, COMPONENT_LABEL, componentNote, costFx, COPY, countryName, detailPlace, detailTitle, FLAG_LABEL,
  grossTone, humanDate, MARKET_LABEL, measureMissing, MISSING_LABEL, money, pct1, personName, rowKey, vatPct,
} from "./format";
import {
  detailSubtitle, floorSourceLabel, floorInCurrency, floorOf, FX_NATIVE_LABEL, FX_SOURCE_LABEL, numberValue, type CurrencyLink, type FloorRow, type Setting,
} from "./config";
import { COGS_DETAIL_COPY, directionClass, directionOf, unitsText, type Direction } from "./cogs_detail";
import { CUSTOMER_COPY, customerLookup, historyText, latestSnapshot, lensLines, type CustomerReads, type LensLine } from "./customer";
import { AS_OF_COPY, changeSource, otherDay, upcomingValue, withinHorizon, type PickFn } from "./as_of";
import { DrawerDateLine, UpcomingFeeNotice } from "./AsOfParts";
import { NewPriceBox } from "./NewPriceBox";
import { SkuMonthsSection } from "./SkuMonthsSection";
import { isPartnerView, viewLookup, type ViewKey, type ViewReads } from "./order_views";
import { COMPETITION_COPY, competitionLine, tcmInputLine } from "./competition";
import { ViewDetail } from "./OrderTypeParts";
import { committedLine, placeOf, savedLine, skuOf, type Refusal } from "./price";
import { overrideFor, type OverridesRead } from "./overrides";
import { DrawerRowLines } from "./RowLines";
import { PriceHistory } from "./PriceHistory";
import { contributionCell, contributionLine, type AdsMode } from "./contribution";
import { findRow, siblings } from "./rows";
import { rrpDetail, rrpLookup, type RrpReads } from "./rrp";
import { SnapshotLine } from "./SnapshotLine";
import { ConfigText } from "./WeeklyMovesSection";
import {
  EVAL_COPY, evalLookup, SNAPSHOT_COPY, STATUS_WORD, TONE_CLASS, type EvalBlock, type EvalLookup, type EvalReads, type RuleVerdict, type SnapshotRule, type StrategySnapshot, verdictFigure,
} from "./strategies";
import { endingLine, movedOff, PROPOSE_COPY } from "./propose";

/** What the edit surface needs: the batch reads and the area name for the permission sentence. Absent, the sheet renders figures only. */
export type EditContext = { runs: PriceRuns | undefined; lastRuns: PriceRun[]; areaName: string; carry?: Carry; overrides?: OverridesRead };
/** A grid cell's save carried into the drawer (C1 v1.57): the row it belongs to, the typed price and C2's refusal, if any. */
export type Carry = { key: string; value: string; refusal: Refusal | null; n: number };

/** The Config margin target as the Price Run read it (C2 v1.7 `settings.margin_target_pct`): null when unset, undefined while it loads. */
export type TargetSetting = Setting | null | undefined;

/** The order type on screen (C1 v1.23): the view, its label and the per-market reads. Absent, Single order. */
export type ViewContext = { view: ViewKey; label: string; reads: ViewReads };

/** The picked date on screen (C1 v1.24) and the run date the COGS are read on. Absent, today. */
export type AsOfContext = { date: string; runDate: string | null };

/** How the sheet picks a date (C1 v1.28): the run date that bounds C2's twelve months, and the Price Run's pick, which reads the
 * date's grids (today's for null) before it writes the date. Absent, the notice and the date line have no button. */
export type PickContext = { runDate: string | null; onPick: PickFn };

/** The sheet chrome. The body is `RowDetail`, rendered on its own in tests because a portal does not render server-side.
 * Width: full on a phone, up to 960px from `sm` (owner ruling 2026-09-29: at least twice the 480px it rendered; px, not rem,
 * because the portal's rem is 20px). Set through the primitive's own `data-[side=right]:` variant, the only form that overrides
 * it (see the drawer-chrome test). */
export function RowDetailSheet({ grids, rowKey: key, onClose, edit, rrps, evals, customers, target, floors, links, views, asOf, pick, ads }: {
  ads?: AdsMode; grids: GridResponse[]; rowKey: string | null; onClose: () => void; edit?: EditContext; rrps?: RrpReads; evals?: EvalReads; customers?: CustomerReads;
  target?: TargetSetting; floors?: FloorRow[]; links?: CurrencyLink[]; views?: ViewContext; asOf?: AsOfContext; pick?: PickContext;
}) {
  const found = key ? findRow(grids, key) : null;
  return (
    <Sheet open={found !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      {found && (
        <SheetContent side="right" className="overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-[960px]" onCloseAutoFocus={(event) => event.preventDefault()}>
          <SheetHeader>
            <SheetTitle>{detailTitle(found.row)}</SheetTitle>
            {/* C1 v1.17: the market and its entity after the place (v26 "Region · Entity"), from the Config currency links. */}
            <SheetDescription>{detailSubtitle(found.row, found.market, links)}</SheetDescription>
          </SheetHeader>
          <RowDetail log grids={grids} row={found.row} market={found.market} edit={edit} rrps={rrps} evals={evals} customers={customers} target={target} floors={floors} ads={ads} views={views} asOf={asOf} pick={pick} />
        </SheetContent>
      )}
    </Sheet>
  );
}

const NA = <span className="text-ks-grey-1">Not yet available</span>;

/** One COGS measure line (CL-019). With `cogs_measures` on the row, the figure or its reason with the date; the driving measure
 * carries the Basis chip. On a grid computed before C4 v1.4 the R1b lines render unchanged (`legacy`). */
function Measure({ basis, active, measure, row, cur, legacy }: {
  basis: Basis; active: Basis; measure: CogsMeasure | undefined; row: GridRow; cur: string | null; legacy: { value: ReactNode; meta: string };
}) {
  const label = BASIS_LABEL[basis];
  if (!measure) return <Line label={label} value={legacy.value} meta={legacy.meta} />;
  const isBasis = basis === active;
  if ("missing" in measure) {
    return <Line label={label} value={<><span className="text-ks-grey-1">{measureMissing(measure.missing, cur)}</span>{isBasis && <> <Badge variant="outline">Basis</Badge></>}</>}
      meta={isBasis ? "never a carried or stand-in cost" : "not the basis · shown for comparison"} />;
  }
  const flag = FLAG_LABEL[measure.flag];
  const source = basis === "current_inventory_cogs" ? `Xero average cost read ${humanDate(measure.effective_date)}` : `production record of ${humanDate(measure.effective_date)}`;
  // C1 v1.39 (C4 v1.13): the units on the inventory line; the direction on the production line, read "Direction only" off the basis.
  const units = basis === "current_inventory_cogs" ? unitsText(measure) : null;
  const dir = basis === "last_production_cogs" ? directionOf(measure) : null;
  return <Line label={label}
    value={<>{money(measure.native_value, measure.native_currency)} {isBasis ? <Badge variant="outline">Basis</Badge> : <Badge variant="outline" className="text-ks-grey-1">{dir ? COGS_DETAIL_COPY.directionOnly : COGS_DETAIL_COPY.notTheBasis}</Badge>}{dir && <> <DirectionChip dir={dir} onBasis={isBasis} date={humanDate(measure.effective_date)} /></>}{flag && <> <Badge variant="outline" className="border-ks-warn text-ks-warn" title={flag.title}>{flag.text}</Badge></>}</>}
    meta={[source, units, row.grain === "store" ? "native" : null].filter(Boolean).join(" · ")} />;
}

/** The production direction chip (C1 v1.39): the arrow and C4's percentage for the eye, the sentence for a screen reader; coloured
 * off the basis only (owner ruling A2); its title names the production record's date (owner ruling A1). */
function DirectionChip({ dir, onBasis, date }: { dir: Direction; onBasis: boolean; date: string }) {
  return <Badge variant="outline" className={directionClass(dir.tone, onBasis)} title={COGS_DETAIL_COPY.title(date)} data-direction={dir.tone}><span aria-hidden>{dir.text}</span><span className="sr-only">{dir.said}</span></Badge>;
}

function Line({ label, value, meta }: { label: string; value: ReactNode; meta?: string }) {
  return (
    <div className="grid grid-cols-[1fr_auto] items-baseline gap-x-3 py-1">
      <span className="text-sm text-ks-grey-1">{label}</span>
      <span className="text-right text-sm text-ks-ink">{value}</span>
      {meta && <span className="col-span-2 text-xs text-ks-grey-1"><ConfigText text={meta} /></span>}
    </div>
  );
}
/** The drawer's Price floor line: the floor with its source and who set it, "No floor set", or, while Config is unread, the
 * sentence C1 v1.1 showed (C2 checks it on save either way). A floor in another currency than the row's says every price needs
 * a reason, as C2 answers (`floor_not_comparable`). */
function FloorLine({ floor, currency }: { floor: FloorRow | null | undefined; currency: string | null }) {
  if (floor === undefined) return <Line label="Price floor" value={<span className="text-ks-grey-1">{COPY.floorLine.unread}</span>} meta={COPY.floorLine.meta} />;
  if (floor === null) return <Line label="Price floor" value={<span className="text-ks-grey-1">{COPY.floorLine.none}</span>} meta={COPY.floorLine.noneMeta} />;
  const rule = floorInCurrency(floor, currency) ? COPY.floorLine.meta : COPY.floorLine.otherMeta(floor.currency ?? "", currency ?? "");
  return <Line label="Price floor" value={money(floor.floor, floor.currency)}
    meta={COPY.floorLine.set(floorSourceLabel(floor.source), personName(floor.set_by), humanDate(floor.set_at), rule)} />;
}
function LensRow({ label, line }: { label: string; line: LensLine }) {
  return <Line label={label} value={line.muted ? <span className="text-ks-grey-1">{line.value}</span> : <span className="tabular-nums">{line.value}</span>} meta={line.meta} />;
}
function H({ children }: { children: ReactNode }) {
  return <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-ks-purple">{children}</h3>;
}
function contributionTile(row: GridRow, ads: AdsMode): ReactNode {
  const c = contributionCell(row.margins, ads);
  return c.kind === "value" ? <span className={c.negative ? "text-ks-danger" : undefined}>{c.text}</span> : <span className="text-ks-grey-1">{c.label}</span>;
}
function Tile({ label, value, tone = "" }: { label: string; value: ReactNode; tone?: string }) {
  return <div className="rounded-md border border-ks-grey-2 p-2"><div className="text-[10px] uppercase text-ks-grey-1">{label}</div><div className={`text-lg font-semibold tabular-nums ${tone}`}>{value}</div></div>;
}

/** The v0.27 detail sections in the v0.27 order, plus the R1b VAT and currency conversion lines. `floors` is the Config read's
 * floor list (C1 v1.16), undefined while it is unread or failed. On today's figures an Amazon row with a coming fee change
 * opens on its notice (C1 v1.28), its component line shows the new figure, and the New price box adds the margins on that date;
 * on a picked date the drawer opens on the date and a way back to today. */
export function RowDetail({ grids, row, market, edit, rrps, evals, customers, target, floors, views, asOf, pick, ads = "included", log = false }: {
  ads?: AdsMode; grids: GridResponse[]; row: GridRow; market: Market; edit?: EditContext; rrps?: RrpReads; evals?: EvalReads; customers?: CustomerReads; target?: TargetSetting;
  floors?: FloorRow[]; views?: ViewContext; asOf?: AsOfContext; pick?: PickContext; log?: boolean;
}) {
  const cur = row.currency ?? row.price?.currency ?? null;
  // The target is the Config value or nothing (C1 v1.9): undefined until read, null when unset; the tile takes no tone without it.
  const targetValue = target ? numberValue(target) : target === null ? null : undefined;
  const cogs = row.cogs;
  // The basis of the grid this row came from (C2 v1.12 envelope); an older envelope names none and is the default.
  const basis: Basis = grids.find((g) => g.grid.market === market)?.grid.basis as Basis | undefined ?? DEFAULT_BASIS;
  const measures = row.cogs_measures;
  const fulfil = row.fulfilment;
  const cogsFlag = cogs ? FLAG_LABEL[cogs.flag] : null;
  const self = rowKey(row);
  const carry = edit?.carry?.key === self ? edit.carry : undefined;
  const sibs = siblings(grids, row, market);
  const committed = edit ? committedLine(edit.lastRuns, row, market) : null;
  // A ship-country row reads its store's RRP (the store price is the one judged).
  const rrp = rrpLookup(rrps, market, skuOf(row), placeOf(row));
  const rrpLine = rrpDetail(rrp, market);
  // The Customer lens at the committed price (C2 v1.15); a ship-country row reads its store's, as the RRP does.
  const customer = customerLookup(customers, market, skuOf(row), placeOf(row));
  const lens = lensLines(customer);
  // The Price history from the same two log entries, so it never contradicts the Previous setpoint; the grid's sentence while unread.
  const history = historyText(customer, Boolean(committed));
  // The latest entry's rule verdicts at commit (C1 v1.14), from the same lens row; none while it is unread or not yet re-read.
  const snapshot = latestSnapshot(customer, committed?.line.committed_at ?? null);
  // The own-channel floor (C1 v1.16): a ship-country row reads its store's, as the RRP does; C2 judges it on save.
  const floor = floorOf(floors, skuOf(row));
  const fx = costFx(row);
  // The order type on screen (C1 v1.23): a ship-country row reads its store's view row, as the RRP does.
  const viewRow = views ? viewLookup(views.reads, market, skuOf(row), placeOf(row)) : null;
  // On a picked date (C1 v1.24) each fee names what moved and from when; on today's figures an Amazon fee names its next change.
  const upcoming = asOf ? null : row.upcoming_fee_change ?? null;
  // The New price box asks C2 for the margins on that date only when C2 serves it (C1 v1.28): within twelve months of the run.
  const feeChange = upcoming && withinHorizon(upcoming.effective_from, pick?.runDate ?? null) ? upcoming : null;
  // The evaluation and the view are read on the picked date (C1 v1.25); one that answered another day names it, as the banner does,
  // beside what it judged: the Strategy section's verdicts (a priced row with a strategy assigned) and the view's figures. A row
  // either judges nothing on says nothing (review round 2).
  const evaluated = evalLookup(evals, market, skuOf(row), placeOf(row));
  const chipsDay = asOf && evaluated.kind === "row" && evaluated.row.price && evaluated.row.strategy?.assigned ? otherDay([evaluated.eval.as_of], asOf.date) : null;
  const viewDay = asOf && viewRow?.kind === "row" && viewRow.row.figures ? otherDay([viewRow.view.as_of], asOf.date) : null;
  const componentMeta = (component: Component) => [
    componentNote(component, row),
    ...(row.changes ?? []).filter((c) => c.component === component && !c.country).map((c) => AS_OF_COPY.componentChange(money(c.today, cur), AS_OF_COPY.from(humanDate(c.effective_from)), changeSource(c))),
  ].join(" · ");
  const comp = competitionLine(row);
  const discountLine = tcmInputLine(row, "avg_discount");
  const refundLine = tcmInputLine(row, "avg_refund");
  const lineValue = (line: { value: string; muted: boolean }) => (line.muted ? <span className="text-ks-grey-1">{line.value}</span> : line.value);
  return (
    <div className="px-4 pb-6">
      {upcoming && (
        <UpcomingFeeNotice fee={upcoming} today={fulfil?.components.find((c) => c.component === upcoming.component)?.value ?? null} currency={cur}
          runDate={pick?.runDate ?? null} onPick={pick?.onPick} />
      )}
      {asOf && <DrawerDateLine date={asOf.date} onPick={pick?.onPick} />}
      {/* c1-price-run-signal-rows (CLB-022): the lines the grid row no longer carries. */}
      {edit && <DrawerRowLines row={row} market={market} saved={savedLine(edit.runs, row, market)} committed={committed?.line ?? null}
        override={overrideFor(edit.overrides, skuOf(row), placeOf(row))} rrp={rrp} areaName={edit.areaName} picked={Boolean(asOf)} />}
      {row.missing.length > 0 && (
        <div role="status" className="mt-3 flex flex-wrap items-center gap-1 text-sm">
          Some figures cannot be worked out: {row.missing.map((m) => <Badge key={m} variant="destructive">{MISSING_LABEL[m]}</Badge>)}
        </div>
      )}
      {/* A view read that answered another day than the one picked names it beside the view (C1 v1.25). */}
      {viewDay !== null && views && <p className="text-xs text-ks-grey-1">{AS_OF_COPY.viewOn(views.label, humanDate(viewDay))}</p>}
      {viewRow && <ViewDetail lookup={viewRow} row={row} />}
      {row.channel === "shopify" && <SkuMonthsSection row={row} />}
      {edit && <div className="mt-3"><NewPriceBox row={row} market={market} runs={edit.runs} areaName={edit.areaName} rrp={rrp} basis={basis} target={targetValue} floor={floor}
        view={views && viewRow ? { lookup: viewRow, partner: isPartnerView(views.view), label: views.label } : undefined} asOf={asOf?.date ?? null} feeChange={feeChange}
        carry={carry} key={carry ? `carry-${carry.n}` : "box"} /></div>}

      <H>COGS detail</H>
      {/* CL-019: both measures with their dates, the driving one carries the Basis chip; never combined, never a stand-in (INV-1). */}
      <p className="text-xs text-ks-grey-1">Margins on the {BASIS_LABEL[basis]} basis · switch the basis in the Price Run toolbar</p>
      {asOf && <p className="text-xs text-ks-ink">{AS_OF_COPY.cogsNote(humanDate(asOf.runDate), humanDate(asOf.date))}</p>}
      <Measure basis="current_inventory_cogs" active={basis} measure={measures?.current_inventory_cogs} row={row} cur={cur}
        legacy={{ value: NA, meta: "needs the daily Xero stock table" }} />
      <Measure basis="last_production_cogs" active={basis} measure={measures?.last_production_cogs} row={row} cur={cur}
        legacy={{ value: cogs ? <>{money(cogs.native_value, cogs.native_currency)} <Badge variant="outline">Basis</Badge>{cogsFlag && <> <Badge variant="outline" className="border-ks-warn text-ks-warn" title={cogsFlag.title}>{cogsFlag.text}</Badge></>}</> : <span className="text-ks-grey-1">{cogsNullReason(row)}</span>,
          meta: cogs ? `production record of ${humanDate(cogs.effective_date)}` : "never a carried or stand-in cost" }} />
      {cogs && cogs.fx_rate != null && (
        <Line label={`Converted to ${cogs.currency}`} value={money(cogs.value, cogs.currency)} meta={`× ${cogs.fx_rate} · ${cogs.fx_source ?? "ECB"} reference rate, ${humanDate(cogs.fx_date)}`} />
      )}
      <Line label="Projected COGS" value={NA} meta="needs Future COGS, not built yet" />
      <Line label="Avg discount cost" value={lineValue(discountLine)} meta={discountLine.meta} />
      <Line label="Avg refund cost" value={lineValue(refundLine)} meta={refundLine.meta} />
      <Line label="Currency" value={cur ?? ABSENT} meta={fx ? COPY.currencyConverted(FX_SOURCE_LABEL[fx] ?? fx) : FX_NATIVE_LABEL} />
      <Line
        label="VAT rate"
        value={row.vat_rate == null ? <span className="text-ks-grey-1">No VAT rate set</span> : vatPct(row.vat_rate)}
        meta={`${countryName(row.location)}${row.net_price != null ? ` · price without VAT ${money(row.net_price, cur)} = revenue` : ""}`}
      />
      <FloorLine floor={floor} currency={row.price?.currency ?? cur} />
      <Line label="RRP" value={rrpLine.value} meta={rrpLine.meta || undefined} />
      <Line label="Order type" value="Single order" meta={views ? COPY.orderTypeOther(views.label) : "list price · the Subscription and partner views are in the Price Run's Order type control"} />

      <H>{asOf ? AS_OF_COPY.componentsOn(humanDate(asOf.date)) : "Fulfilment fee components at the current price"}</H>
      {(fulfil?.components ?? []).map((c) => (
        <Line key={c.component} label={COMPONENT_LABEL[c.component]}
          value={<>{c.value == null ? <span className="text-ks-grey-1">No cost yet</span> : money(c.value, cur)}{upcoming?.component === c.component && (
            <span className="text-ks-purple"> {AS_OF_COPY.arrow(upcomingValue(upcoming), humanDate(upcoming.effective_from))}</span>
          )}</>}
          meta={componentMeta(c.component)} />
      ))}
      {/* A store has no components of its own (its figures fold its ship countries'), so on a picked date it names what moved in
          each country, as its cell's marker does (review round 2). */}
      {asOf && row.grain === "store" && (row.changes ?? []).map((c) => (
        <Line key={`${c.component}-${c.country ?? ""}`} label={`${COMPONENT_LABEL[c.component]}${c.country ? `, ${countryName(c.country)}` : ""}`} value={money(c.as_of, cur)}
          meta={AS_OF_COPY.componentChange(money(c.today, cur), AS_OF_COPY.from(humanDate(c.effective_from)), changeSource(c))} />
      ))}
      <Line label="Total at this price" value={money(fulfil?.total, cur)} meta={detailPlace(row, market)} />
      <p className="mt-2 text-xs text-ks-grey-1">
        Fulfilment values are without VAT.{row.channel === "amazon" ? " Amazon charges its commission on the VAT inclusive price." : ""} The margins below are computed on the <b>{BASIS_LABEL[basis]}</b> basis.
      </p>

      <H>{row.channel === "amazon" ? `Marketplace prices, ${MARKET_LABEL[market]}` : `Country prices, Shopify ${MARKET_LABEL[market]}`}</H>
      <p className="text-xs text-ks-grey-1">{row.channel === "amazon" ? "Each marketplace holds its own price. Overrides with a reason and an expiry are not built yet." : "One store price reaches every ship country; the VAT rate and the courier differ."}</p>
      <Table>
        <TableHeader><TableRow><TableHead>{row.channel === "amazon" ? "Marketplace" : "Ships to"}</TableHead><TableHead className="text-right">Price</TableHead><TableHead className="text-right">Gross</TableHead></TableRow></TableHeader>
        <TableBody>
          {sibs.map((s) => (
            <TableRow key={rowKey(s)} className={rowKey(s) === self ? "font-bold" : undefined}>
              <TableCell>{row.channel === "amazon" ? s.marketplace_name : countryName(s.location)}</TableCell>
              <TableCell className="text-right tabular-nums">{s.price ? money(s.price.value, s.price.currency) : MISSING_LABEL.no_price}</TableCell>
              <TableCell className="text-right tabular-nums">{pct1(s.margins.gross_pct)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <H>Productivity lens</H>
      <p className="text-xs text-ks-grey-1">Single order · {BASIS_LABEL[basis]}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label={ads === "excluded" ? "Contribution margin (ex ads)" : "Contribution margin"} value={contributionTile(row, ads)} />
        <Tile label="Fulfilment margin" value={pct1(row.margins.fulfilment_pct)} />
        <Tile label="Gross margin" value={pct1(row.margins.gross_pct)} tone={grossTone(row.margins.gross_pct, targetValue)} />
        <Tile label="Fees %" value={pct1(row.margins.fees_pct)} />
      </div>
      <p className="text-xs text-ks-grey-1">COGS + fulfilment is {pct1(row.margins.cogs_fulfilment_pct)} of revenue.</p>
      <p className="text-xs text-ks-grey-1" data-contribution-line>{contributionLine(row.margins, ads)}</p>

      <H>Strategy</H>
      {/* An evaluation that answered another day than the one picked names it (C1 v1.25). */}
      {chipsDay !== null && <p className="text-xs text-ks-grey-1">{AS_OF_COPY.judgedOn(humanDate(chipsDay))}</p>}
      <StrategySection lookup={evaluated} strategy={row.strategy} edit={Boolean(edit)} saved={edit ? savedLine(edit.runs, row, market) : null} market={market} />

      <H>Customer lens</H>
      {lens.pending && <p className="text-xs font-semibold text-ks-warn" data-customer-pending>{lens.pending}</p>}
      <LensRow label={CUSTOMER_COPY.previous} line={lens.previous} />
      <LensRow label={CUSTOMER_COPY.channel} line={lens.channel} />
      <LensRow label={CUSTOMER_COPY.servingLabel} line={lens.serving.serving} />
      <LensRow label={CUSTOMER_COPY.dayLabel} line={lens.serving.day} />
      <LensRow label={CUSTOMER_COPY.monthLabel} line={lens.serving.month} />
      <LensRow label={CUSTOMER_COPY.unitLabel} line={lens.serving.unit} />

      <H>Competition lens</H>
      <Line label={COMPETITION_COPY.label} value={lineValue(comp)} meta={comp.meta} />

      <H>Target</H>
      <Line label="Margin target" value={targetValue != null ? COPY.detailTarget(targetValue) : <span className="text-ks-grey-1">{target === null || (target && target.not_set) ? <ConfigText text={target?.not_set ?? "Target not set in Config"} /> : COPY.detailTargetReading}</span>} meta="drives the Below target flag · set in Config" />

      <H>Price history</H>
      <p className="text-sm text-ks-grey-1">
        {committed && <>{COPY.historyCommitted(money(committed.line.new_price, committed.line.currency), humanDate(committed.line.committed_at), personName(committed.run.committed_by), committed.line.floor_override_reason)}<br /></>}
        {history ?? (row.price ? (committed ? COPY.historyEarlier : `One entry: ${money(row.price.value, row.price.currency)}, set ${humanDate(row.price.set_at)}, source ${row.price.source === "legacy_sheet" ? "legacy price sheet" : row.price.source}.`) : (committed ? "" : "No price in the log."))}
      </p>
      {/* The last prices from the Price log (C1 v1.35), asked by the sheet (`log`); a ship-country row reads its store's, as the lens does. */}
      {log && <PriceHistory market={market} sku={skuOf(row)} place={placeOf(row)} />}
      {snapshot !== undefined && <SnapshotSection snapshot={snapshot} />}

      <Separator className="my-4" />
      <div className="rounded-md border border-dashed border-ks-grey-2 p-3 text-xs"><b>How prices change today</b><br />{COPY.howPricesChange}</div>
    </div>
  );
}

/**
 * The drawer's Strategy section (approved design M25 drawer 3003): one row per rule with its mark and "now x%", the goal with
 * "now x%, target y%", as C2 judged the row at its effective price (C2 v1.14 bulk read). The suggested price belongs to the New
 * price box, which asks C2 at the typed price. The browser judges nothing (H12).
 */
function StrategySection({ lookup, strategy, edit, saved = null, market }: { lookup: EvalLookup; strategy: string | null; edit: boolean; saved?: PriceRunLine | null; market: Market }) {
  const none = <span className="text-ks-grey-1">{EVAL_COPY.noStrategy}</span>;
  const proposal = (s: EvalBlock | null = null) => saved?.proposal ? <ProposalLine s={s} saved={saved} market={market} /> : null;
  if (lookup.kind === "pending") return <><Line label="Assigned strategy" value={strategy ?? none} meta={strategy ? EVAL_COPY.reading : undefined} />{proposal()}</>;
  if (lookup.kind === "failed") return <><Line label="Assigned strategy" value={strategy ?? none} meta={strategy ? EVAL_COPY.unreadDetail : undefined} />{proposal()}</>;
  if (lookup.kind === "none") {
    return <><Line label="Assigned strategy" value={lookup.eval.strategised ? none : <span className="text-ks-grey-1">{EVAL_COPY.notApplied}</span>} meta={lookup.eval.not_strategised ?? undefined} />{proposal()}</>;
  }
  const r = lookup.row;
  const s = r.strategy;
  if (!s || !r.price) return <><Line label="Assigned strategy" value={strategy ?? none} meta={r.no_price ?? EVAL_COPY.noPrice} />{proposal(s)}</>;
  if (!s.assigned) return <><Line label="Assigned strategy" value={none} meta={s.not_assigned ?? s.not_strategised ?? undefined} />{proposal(s)}</>;
  return (
    <>
      <Line label="Assigned strategy" value={<>{s.assigned.name} {s.tone && s.summary && <Badge variant="outline" className={TONE_CLASS[s.tone]}>{s.summary}</Badge>}{!s.tone && s.summary && <span className="text-ks-grey-1"> · {s.summary}</span>}</>}
        meta={`${EVAL_COPY.inherited(s.assigned.marketplace_id, r.channel)} · ${EVAL_COPY.at(r.price.pending, money(r.price.value, r.price.currency))}`} />
      {s.rules.map((rule) => (
        <RuleLine key={rule.rule_id} rule={rule} figure={rule.value != null ? EVAL_COPY.now(rule.value, rule.currency) : null}
          meta={rule.dropped && rule.dropped_reason ? EVAL_COPY.dropped(rule.dropped_reason) : rule.reason ?? undefined} />
      ))}
      {s.goal && <Line label="Goal" value={s.goal.text} meta={s.goal.value != null && s.goal.target != null ? EVAL_COPY.goalNow(s.goal.value, s.goal.target, s.goal.currency) : s.goal.reason ?? undefined} />}
      {proposal(s)}
      {edit && <p className="text-xs text-ks-grey-1">{EVAL_COPY.inBox}</p>}
    </>
  );
}

/**
 * c1-run-propose (§9.10 drawer Strategy section, mockup v28 frame 3): the proposal the strategy run saved on this line and its saved rule
 * check (DEC-1366, DEC-1367, DEC-1368, DEC-1403 P9-COPY-2 behaviour 5). All of it was judged at the proposed price and stays true after
 * the person moves the line. The verdicts above are live, at the saved price. An answer from before C2 v1.76 has no check, so it names
 * only the price and the live unverified rules. Code-generated text only.
 */
function ProposalLine({ s, saved, market }: { s: EvalBlock | null; saved: PriceRunLine; market: Market }) {
  const proposal = saved.proposal as NonNullable<PriceRunLine["proposal"]>;
  const c = saved.currency;
  const parts = [
    PROPOSE_COPY.proposedBy(money(proposal.price, c)),
    proposal.goal_price ? PROPOSE_COPY.goalPrice(money(proposal.goal_price, c)) : null,
    endingLine(proposal.ending, market),
    proposal.window ? PROPOSE_COPY.window(money(proposal.window.low, c), proposal.window.high == null ? null : money(proposal.window.high, c)) : null,
  ].filter(Boolean);
  const unverified = proposal.unverified ?? s?.unverified ?? s?.rules.filter((r) => r.status === "no_data") ?? [];
  const notMet = (proposal.not_met ?? []).map((r) => `${EVAL_COPY.rule(r.priority)} ${r.text}${r.value == null ? "" : ` (${verdictFigure(r.value, r.currency)})`}`);
  return (
    <div data-proposal-drawer>
      <Line label={PROPOSE_COPY.proposalHead} value={parts.join(" · ")}
        meta={movedOff(saved) ? `${PROPOSE_COPY.strategyWas(money(proposal.price, c))} · now ${money(saved.new_price, c)}` : undefined} />
      {notMet.length > 0 && <p className="text-xs text-ks-grey-1" data-proposal-not-met>{PROPOSE_COPY.notMetNamed(notMet)}</p>}
      {(proposal.dropped ?? []).map((r) => <p key={r.rule_id} className="text-xs text-ks-grey-1" data-proposal-dropped>{EVAL_COPY.rule(r.priority)} {r.label}: {EVAL_COPY.dropped(r.reason)}</p>)}
      {unverified.length > 0 && <p className="text-xs text-ks-grey-1">{PROPOSE_COPY.unverifiedNamed(unverified.map((u) => `${EVAL_COPY.rule(u.priority)} ${u.label}`))}</p>}
    </div>
  );
}

/** One rule and its mark (met / not met / n/a / no data), with its figure and the sentence under it: the live verdict in the
 * Strategy section, the recorded one in the Price history. */
function RuleLine({ rule, figure, meta }: { rule: RuleVerdict | SnapshotRule; figure: string | null; meta: string | undefined }) {
  return (
    <Line label={EVAL_COPY.rule(rule.priority)}
      value={<>{rule.text} <Badge variant="outline" className={rule.status === "met" ? TONE_CLASS.pass : rule.status === "not_met" ? TONE_CLASS.fail : "text-ks-grey-1"}>{STATUS_WORD[rule.status]}</Badge>{figure != null && <span className="tabular-nums text-ks-grey-1"> {figure}</span>}</>}
      meta={meta} />
  );
}

/**
 * The latest Price history entry's rule verdicts (C1 v1.14, owner ruling 2026-09-29): the line the Logs Price pane shows, then
 * each rule's result and the goal as C2 recorded them when the price was committed (C2 v1.16, on the Last Production COGS
 * basis). A snapshot is a record, so it is never re-judged (H12) and its figures read "at commit", not "now".
 */
function SnapshotSection({ snapshot }: { snapshot: StrategySnapshot | null }) {
  const judged = snapshot?.assigned && !snapshot.not_evaluated ? snapshot : null;
  return (
    <div className="mt-2 rounded-md border border-ks-grey-2 p-2">
      <div className="text-xs font-semibold text-ks-grey-1">{SNAPSHOT_COPY.heading}</div>
      <SnapshotLine snapshot={snapshot} />
      {judged && (
        <>
          <p className="text-xs text-ks-grey-1">{SNAPSHOT_COPY.judged(money(judged.price.value, judged.price.currency), BASIS_LABEL[judged.cogs_basis] ?? judged.cogs_basis, humanDate(judged.as_of))}</p>
          {judged.rules.map((rule) => (
            <RuleLine key={rule.rule_id} rule={rule} figure={rule.value != null ? SNAPSHOT_COPY.figure(rule.value, rule.currency) : null} meta={rule.reason ?? undefined} />
          ))}
          {judged.goal && <Line label="Goal" value={judged.goal.text} meta={judged.goal.value != null && judged.goal.target != null ? SNAPSHOT_COPY.goalAt(judged.goal.value, judged.goal.target, judged.goal.currency) : judged.goal.reason ?? undefined} />}
        </>
      )}
    </div>
  );
}
